import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CreateModuleDefinitionDto } from "../../../../model/module/dto/module-definition/CreateModuleDefinitionDto";
import { UpdateModuleDefinitionDto } from "../../../../model/module/dto/module-definition/UpdateModuleDefinitionDto";
import isEmpty from "lodash/isEmpty";
import { moduleActions } from "../../../../redux/module/module.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { useUnsavedChangesWarningModalContext } from "../../../campaign/components/unsaved-changes-warning-modal/unsaved-changes-warning-modal-context";
import { moduleSliceSelectors } from "../../../../redux/module/module.selector";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import omitBy from "lodash/omitBy";
import isNil from "lodash/isNil";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { onSetTypedCRUDErrors } from "../../../../utils/onSetTypedCRUDErrors";
import type { StepsProps } from "../../../../utils/local-antd";
import { ModuleDefinitionCreateEditBasicInfoStep } from "../steps/ModuleDefinitionCreateEditBasicInfoStep";
import { ModuleDefinitionCreateEditFieldsStep } from "../steps/ModuleDefinitionCreateEditFieldsStep";
import { getModuleTemplateInputs } from "../utils/moduleUtils";
import { ModuleDefinitionTemplates } from "../../../../model/module/types/module-definition/ModuleDefinitionTemplates";
import omit from "lodash/omit";
import { thresholdSliceSelectors } from "../../../../redux/threshold/threshold.selector";
import { DrawerWithStepper } from "../../../../components/DrawerWithStepper";
import { ModuleDefinitionKind } from "../../../../model/module/types/module-definition/ModuleDefinitionKind";
import { ModuleDefinitionCreateEditTemplatesStep } from "./ModuleDefinitionCreateEditTemplatesStep";
import { ModuleDefinitionCreateEditConfigStep } from "../steps/ModuleDefinitionCreateEditConfigStep";
import { userActions } from "../../../../redux/user/user.slice";
import { thresholdActions } from "../../../../redux/threshold/threshold.slice";
import { TestIDs } from "../../../../tests/types/testIds";

type ModuleDefinitionCreateEditDrawerProps = {
  open: boolean;
  onClose: VoidFunction;
};

export const ModuleDefinitionCreateEditDrawer: React.FC<
  ModuleDefinitionCreateEditDrawerProps
> = ({ open = false, onClose }) => {
  const [submitLoading, setSubmitLoading] = useState(false);

  const { t } = useTranslation();
  const { openUnsavedChangesModal } = useUnsavedChangesWarningModalContext();
  const formMethods = useForm<
    (CreateModuleDefinitionDto | UpdateModuleDefinitionDto) & {
      template?: ModuleDefinitionTemplates;
    }
  >();
  const dispatch = useAppDispatch();

  const {
    reset,
    setError,
    formState: { dirtyFields },
  } = formMethods;

  const selectedModuelDefinition = useAppSelector(
    moduleSliceSelectors.selectSelectedModuleDefinition,
  );
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const thresholds = useAppSelector(
    thresholdSliceSelectors.selectThresholdStats,
  );

  const canCreateModuleDefinition =
    (thresholds?.consumed?.MODULES_DEFINITIONS || 0) <
    (thresholds?.total?.MODULES_DEFINITIONS || 0);

  const editMode =
    !isEmpty(selectedModuelDefinition) &&
    !isEmpty(selectedModuelDefinition?._id);

  const stepperItems: StepsProps["items"] = [
    ...(!editMode ? [{ title: t("modules.stepper.getStarted.title") }] : []),
    { title: t("modules.stepper.basicInfo.title") },
    ...(!editMode ? [{ title: t("modules.stepper.templates.title") }] : []),
    { title: t("modules.stepper.fields.title") },
  ];

  const handleWarningModalConfirm = () => {
    formMethods.reset();
    dispatch(moduleActions.setSelectedModuleDefinition(null));
    onClose();
  };

  const onLocalClose = () => {
    if (!isEmpty(formMethods.formState.dirtyFields)) {
      openUnsavedChangesModal(handleWarningModalConfirm);
      return;
    }

    handleWarningModalConfirm();
  };

  const onCreateModuleDefinition = () => {
    const payload = {
      ...formMethods.getValues(),
      userId,
      inputs: formMethods
        .getValues("inputs")
        ?.map((x, i) =>
          omit({ ...x, type: x.type?.toLowerCase(), order: i }, ["icon", "id"]),
        ),
    } as CreateModuleDefinitionDto & {
      template?: ModuleDefinitionTemplates;
    };

    if (payload?.template) {
      delete payload.template;
    }

    return dispatch(moduleActions.createModuleDefinition(payload)).unwrap();
  };

  const onEditModuleDefinition = () => {
    const dto = formMethods.getValues() as UpdateModuleDefinitionDto;

    const updateDto = omitBy(
      dto,
      isNil,
    ) as unknown as UpdateModuleDefinitionDto & {
      template?: ModuleDefinitionTemplates;
    };

    const modifiedInputs = formMethods
      .getValues("inputs")
      ?.map((x, i) =>
        omit({ ...x, type: x.type?.toLowerCase(), order: i }, ["icon", "id"]),
      );

    updateDto.inputs = modifiedInputs as UpdateModuleDefinitionDto["inputs"];

    if (
      updateDto.icon?.name === selectedModuelDefinition?.icon?.name &&
      updateDto.icon?.prefix === selectedModuelDefinition?.icon?.prefix
    ) {
      delete updateDto.icon;
    }

    if (updateDto?.template) {
      delete updateDto.template;
    }

    return dispatch(moduleActions.updateModuleDefinition(updateDto)).unwrap();
  };

  const onSubmit = async () => {
    try {
      setSubmitLoading(true);

      if (editMode) {
        await onEditModuleDefinition();
      } else {
        await onCreateModuleDefinition();
        await dispatch(thresholdActions.getThresholds({ userId })).unwrap();
      }

      await dispatch(
        moduleActions.getAllModuleDefinitions({ userId }),
      ).unwrap();

      onClose();
      reset();

      if (editMode) {
        dispatch(moduleActions.setSelectedModuleDefinition(null));
        AtiraToast.success(t("modules.definition.update.success"));
      } else {
        AtiraToast.success(t("modules.definition.create.success"));
      }

      dispatch(userActions.getLoggedInUser({ userId }));
      formMethods.reset();
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e, { duration: 8 });
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isEmpty(dirtyFields)) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [dirtyFields]);

  useEffect(() => {
    if (!canCreateModuleDefinition && !editMode) {
      onClose();
    }
  }, [editMode, onClose, canCreateModuleDefinition]);

  useEffect(() => {
    if (!open) {
      return;
    }

    reset({
      name: selectedModuelDefinition?.name || "",
      description: selectedModuelDefinition?.description || "",
      inputs:
        selectedModuelDefinition?.inputs ||
        getModuleTemplateInputs(ModuleDefinitionTemplates.DEFAULT),
      moduleDefinitionId: selectedModuelDefinition?._id || undefined,
      userId,
      kind: selectedModuelDefinition?.kind || ModuleDefinitionKind.DYNAMIC,
      icon: selectedModuelDefinition?.icon,
    });
  }, [open, selectedModuelDefinition, userId, reset]);

  return (
    <FormProvider {...formMethods}>
      <DrawerWithStepper
        open={open}
        onClose={onLocalClose}
        title={t(`modules.definition.${editMode ? "update" : "create"}.title`, {
          name: selectedModuelDefinition?.name,
        })}
        submitLoading={submitLoading}
        onSubmit={onSubmit}
        steps={[
          ...(!editMode ? [<ModuleDefinitionCreateEditConfigStep />] : []),
          <ModuleDefinitionCreateEditBasicInfoStep />,
          ...(!editMode ? [<ModuleDefinitionCreateEditTemplatesStep />] : []),
          <ModuleDefinitionCreateEditFieldsStep />,
        ]}
        stepperItems={stepperItems}
        editMode={editMode}
        nextButtonDataTestId={TestIDs.modules.createEdit.next}
        prevButtonDataTestId={TestIDs.modules.createEdit.previous}
      />
    </FormProvider>
  );
};
